import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import {
  surgeries,
  specialties,
  surgeryTypes,
  procedureTypes,
  type ProcedureType,
  type Surgery,
} from "@/lib/healic-data";
import surgeryHero from "@/assets/surgery-hero.jpg";

export const Route = createFileRoute("/surgeries/")({
  head: () => ({
    meta: [
      { title: "Surgeries & Procedures at Healic — Fixed-Price Care" },
      {
        name: "description",
        content:
          "Browse all surgeries at Healic — laser piles, hernia, gallbladder, knee replacement, cataract, hysterectomy, kidney stones and more. Fixed-price packages with insurance support.",
      },
      { property: "og:title", content: "Surgeries & Procedures — Healic" },
      { property: "og:description", content: "All surgeries offered at Healic hospitals with transparent pricing." },
      { property: "og:url", content: "https://healic.in/surgeries" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/surgeries" }],
  }),
  component: SurgeriesIndex,
});

function SurgeriesIndex() {
  const [query, setQuery] = useState("");
  const [specialtySlug, setSpecialtySlug] = useState<string>("all");
  const [type, setType] = useState<ProcedureType | "all">("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return surgeries.filter((s) => {
      if (specialtySlug !== "all" && s.specialtySlug !== specialtySlug) return false;
      if (type !== "all" && !(surgeryTypes[s.slug] ?? []).includes(type)) return false;
      if (q) {
        const haystack = `${s.name} ${s.short} ${s.overview}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [query, specialtySlug, type]);

  const grouped = useMemo(() => {
    return specialties
      .map((sp) => ({
        specialty: sp,
        items: filtered.filter((s) => s.specialtySlug === sp.slug),
      }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  const hasFilters = query !== "" || specialtySlug !== "all" || type !== "all";

  return (
    <PageShell>
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${surgeryHero})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/95 to-primary/70" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">All Procedures</p>
          <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Surgeries & Treatments</h1>
          <p className="mt-4 max-w-2xl opacity-85">
            Modern, minimally-invasive surgery across all specialties — quoted upfront with no billing surprises.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-10 lg:px-8">
        <div className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-6">
          <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr_1fr_auto]">
            <label className="relative flex items-center">
              <Search className="absolute left-4 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search surgery — e.g. cataract, knee, piles…"
                className="w-full rounded-pill border border-border bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                aria-label="Search surgeries"
              />
            </label>

            <select
              value={specialtySlug}
              onChange={(e) => setSpecialtySlug(e.target.value)}
              className="w-full rounded-pill border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              aria-label="Filter by specialty"
            >
              <option value="all">All specialties</option>
              {specialties.map((sp) => (
                <option key={sp.slug} value={sp.slug}>
                  {sp.name}
                </option>
              ))}
            </select>

            <select
              value={type}
              onChange={(e) => setType(e.target.value as ProcedureType | "all")}
              className="w-full rounded-pill border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              aria-label="Filter by procedure type"
            >
              <option value="all">All procedure types</option>
              {procedureTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setSpecialtySlug("all");
                  setType("all");
                }}
                className="inline-flex items-center justify-center gap-1.5 rounded-pill bg-cream px-4 py-3 text-sm font-semibold text-foreground hover:bg-accent/10"
              >
                <X className="h-4 w-4" /> Clear
              </button>
            )}
          </div>

          {/* Quick chips for procedure type */}
          <div className="mt-4 flex flex-wrap gap-2">
            {(["all", ...procedureTypes] as const).map((t) => {
              const active = type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`rounded-pill px-3.5 py-1.5 text-xs font-semibold transition ${
                    active
                      ? "bg-accent text-accent-foreground"
                      : "bg-cream text-foreground hover:bg-accent/10"
                  }`}
                >
                  {t === "all" ? "All types" : t}
                </button>
              );
            })}
          </div>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of {surgeries.length} procedures
        </p>
      </section>

      <section className="mx-auto max-w-7xl space-y-14 px-4 py-10 pb-16 lg:px-8">
        {grouped.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-card p-12 text-center">
            <h2 className="font-display text-xl font-semibold">No surgeries match your filters</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Try clearing filters or searching with a different term.
            </p>
          </div>
        ) : (
          grouped.map(({ specialty, items }) => (
            <div key={specialty.slug}>
              <div className="flex items-end justify-between">
                <h2 className="font-display text-2xl font-bold">{specialty.name}</h2>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: specialty.slug }}
                  className="text-sm font-semibold text-accent"
                >
                  View specialty →
                </Link>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((s: Surgery) => {
                  const tags = surgeryTypes[s.slug] ?? [];
                  return (
                    <Link
                      key={s.slug}
                      to="/surgeries/$slug"
                      params={{ slug: s.slug }}
                      className="group flex flex-col rounded-3xl border border-border bg-card p-6 shadow-soft transition hover:border-accent"
                    >
                      <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                      <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.short}</p>
                      {tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {tags.map((t) => (
                            <span
                              key={t}
                              className="rounded-pill bg-primary-soft px-2.5 py-0.5 text-[11px] font-semibold text-primary"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </section>
    </PageShell>
  );
}
